/**
 * Bounding-region-based PDF highlight utility.
 *
 * Uses pre-computed polygon coordinates from the ingestion pipeline
 * (stored in chunk.bounding_regions) to create precise highlights
 * without text-search matching.
 *
 * Falls back gracefully: if no bounding regions are available,
 * returns null so the caller can use text-search highlighting instead.
 *
 * Usage:
 *   const highlight = createHighlightFromBoundingRegions(boundingRegions, pageNumber, viewport)
 *   if (highlight) {
 *     // use bounding-region highlight (precise)
 *   } else {
 *     // fall back to text-search highlight
 *   }
 */

import type { BoundingRegion } from "@/types/questionnaire/models"
import type { IHighlight } from "react-pdf-highlighter"
import type { PdfViewportInfo } from "./pdfHighlightUtils"

export type { BoundingRegion }

/**
 * Create a highlight from bounding regions (polygon coordinates).
 *
 * Returns null if no valid bounding regions are provided,
 * so the caller can fall back to text-search highlighting.
 *
 * @param boundingRegions - Array of bounding regions from chunk metadata
 * @param pageNumber - The PDF page number to highlight on
 * @param viewport - PDF viewport info for coordinate conversion
 * @param highlightText - Optional text to display in the highlight
 */
export function createHighlightFromBoundingRegions(
  boundingRegions: BoundingRegion[] | null | undefined,
  pageNumber: number,
  viewport: PdfViewportInfo,
  highlightText?: string,
): IHighlight | null {
  if (!boundingRegions || boundingRegions.length === 0) {
    return null
  }

  // Filter regions for the requested page
  const pageRegions = boundingRegions.filter(
    (r) => r.pageNumber === pageNumber && r.polygon && r.polygon.length === 8,
  )

  if (pageRegions.length === 0) {
    return null
  }

  // Convert each polygon to viewport coordinates
  const viewportRects = pageRegions.map((region) => {
    const p = region.polygon

    // Polygon: [x1,y1, x2,y2, x3,y3, x4,y4] — 4 corners
    // Use min/max to get bounding box regardless of corner order
    const xCoords = [p[0], p[2], p[4], p[6]]
    const yCoords = [p[1], p[3], p[5], p[7]]
    const [vx1, vy1, vx2, vy2] = viewport.convertToViewportRectangle([
      Math.min(...xCoords),
      Math.min(...yCoords),
      Math.max(...xCoords),
      Math.max(...yCoords),
    ])

    return {
      x1: Math.min(vx1, vx2),
      y1: Math.min(vy1, vy2),
      x2: Math.max(vx1, vx2),
      y2: Math.max(vy1, vy2),
    }
  })

  // Compute bounding rect across all regions
  const boundingRect = viewportRects.reduce(
    (acc, rect) => ({
      x1: Math.min(acc.x1, rect.x1),
      y1: Math.min(acc.y1, rect.y1),
      x2: Math.max(acc.x2, rect.x2),
      y2: Math.max(acc.y2, rect.y2),
      width: viewport.width,
      height: viewport.height,
      pageNumber,
    }),
    {
      x1: Infinity,
      y1: Infinity,
      x2: -Infinity,
      y2: -Infinity,
      width: viewport.width,
      height: viewport.height,
      pageNumber,
    },
  )

  const scaledRects = viewportRects.map((rect) => ({
    ...rect,
    width: viewport.width,
    height: viewport.height,
    pageNumber,
  }))

  return {
    id: `bbox-highlight-${pageNumber}-${Date.now()}`,
    content: {
      text: highlightText ?? "",
    },
    position: {
      pageNumber,
      boundingRect,
      rects: scaledRects,
    },
    comment: {
      text: "Bounding region match",
      emoji: "",
    },
  }
}

/**
 * Check if a citation has bounding regions available.
 * Use this to decide whether to use bounding-region or text-search highlighting.
 */
export function hasBoundingRegions(
  boundingRegions: BoundingRegion[] | null | undefined,
): boolean {
  return (
    Array.isArray(boundingRegions) &&
    boundingRegions.length > 0 &&
    boundingRegions.some((r) => r.polygon && r.polygon.length === 8)
  )
}
